// BabbleBlog content. Add new posts to this array — slug must be unique and
// lowercase-kebab. `date` is ISO (YYYY-MM-DD) and used for sort + JSON-LD.
// Body is markdown rendered with react-markdown.

import coverWordleAlternatives from "@/assets/blog/cover-wordle-alternatives.jpg";
import coverCognitiveHealth from "@/assets/blog/cover-cognitive-health.jpg";
import coverHistoryOfWordGames from "@/assets/blog/cover-history-of-word-games.jpg";

export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  date: string;
  readingMinutes: number;
  tags: string[];
  category: string;
  coverImage: string;
  coverAlt: string;
  body: string;
}

export const POSTS: BlogPost[] = [
  {
    slug: "10-daily-word-games-like-wordle",
    title: "10 Daily Word Games Like Wordle (and Why BabbleGram Belongs on the List)",
    description:
      "A curated list of the 10 best daily word games to play if you love Wordle — from Connections and Spelling Bee to BabbleGram, the daily word chain puzzle.",
    date: "2026-05-13",
    readingMinutes: 7,
    tags: ["wordle alternative", "word games", "daily puzzles"],
    category: "Word Games",
    coverImage: coverWordleAlternatives,
    coverAlt: "Colorful Scrabble-style letter tiles arranged in a vertical chain on a cream paper background.",
    body: `Looking for more than just one five-letter word a day? In 2026, the "daily puzzle habit" has evolved into a full-scale mental workout. Here are 10 of the best alternatives to keep your brain sharp and your streaks alive.

## 1. Quordle
If one word feels like a warm-up, try four. **Quordle** tasks you with solving four hidden words at once using the same guesses. It's a high-stakes balancing act for serious word lovers.

- **Play here:** [merriam-webster.com/games/quordle](https://www.merriam-webster.com/games/quordle)

## 2. Waffle
**Waffle** gives you a grid of letters that are already on the board — they're just in the wrong places. You have 15 swaps to find six words in a crossword-style layout. It's visual, satisfying, and perfect for pattern recognition.

- **Play here:** [wafflegame.net](https://wafflegame.net)

## 3. Contexto
Forget letters; **Contexto** is about meaning. Using an AI algorithm, the game tells you how "close" your guess is to the secret word based on context. It's a fascinating way to test your vocabulary through association.

- **Play here:** [contexto.me](https://contexto.me)

## 4. Worldle
A must-play for geography buffs. You're shown the silhouette of a country or territory and must guess its name. After each attempt, you get the distance and direction toward the correct location.

- **Play here:** [worldle.teuteuf.fr](https://worldle.teuteuf.fr)

## 5. Semantle
If you want a challenge that might take all day, **Semantle** is the hardcore version of semantic guessing. With a massive dictionary and zero clues other than "closeness," it's the ultimate slow-burn puzzle.

- **Play here:** [semantle.com](https://semantle.com)

## 6. Squaredle
Think of this as a daily word search with a twist. You connect adjacent letters in a grid to find as many words as possible. The goal is to clear the board and find the elusive "Word of the Day."

- **Play here:** [squaredle.app](https://squaredle.app)

## 7. Bandle
For the music obsessed, **Bandle** starts with just one instrument (usually the drums) and adds more with every wrong guess. It's a brilliant test of your ear for famous song arrangements.

- **Play here:** [bandle.app](https://bandle.app)

## 8. Framed
**Framed** is the daily ritual for cinephiles. You're shown a single frame from a movie; guess wrong, and you get another shot with a more recognizable scene. It's as much an art gallery as it is a game.

- **Play here:** [framed.wtf](https://framed.wtf)

## 9. Polygonle
**Polygonle** uses shapes to represent letters. If two slots share a polygon, they must share the same letter. It combines the vocabulary of a word game with the logical deduction of a cryptogram.

- **Play here:** [polygonle.com](https://polygonle.com)

## 10. BabbleGram
We're ending the list with **our very own game**, and we won't hide the bias: we think it's a fantastic addition to your routine. **BabbleGram** is a sequential word-builder where you start with a word and add one letter at a time to create a chain of new words. It's fast, incredibly rewarding, and designed to make you think about word structure in a whole new way.

- **Play here:** [babblegram.app](/)

---

Whether you're into geography, music, or pure linguistics, these daily challenges offer the perfect mental reset. Give them a try and find your next favorite habit!`,
  },
  {
    slug: "benefits-of-word-games-cognitive-health",
    title: "The Cognitive Health Benefits of Daily Word Games",
    description:
      "What the research says about word games, memory, vocabulary, and long-term brain health — and how a daily puzzle habit fits in.",
    date: "2026-05-10",
    readingMinutes: 6,
    tags: ["cognitive health", "brain games", "word games"],
    category: "Cognitive Health",
    coverImage: coverCognitiveHealth,
    coverAlt: "Editorial illustration of a brain made from overlapping wooden letterforms in red, blue, and cream tones.",
    body: `## Is Your Brain Secretly a Time Traveler? 🧠✨

Forget the expensive green juices and "biohacking" gadgets. If you want to keep your brain from acting its age, the secret might be sitting right in your Sunday paper (or on your phone).

According to a massive study of over 19,000 people, regular word-puzzlers have brains that function like they are **10 years younger** than their actual age. That's right—your daily Wordle or crossword habit is basically a biological "undo" button for your cognitive clock.

### Why Your Brain Loves the "Aha!" Moment

It isn't just about learning fancy words like *syzygy*. Here is what's happening under the hood when you're hunting for that five-letter synonym for "joy":

- **The 10-Year Rewind:** In tests of grammatical reasoning and short-term memory, frequent puzzlers performed at the level of people a full decade their junior.
- **The Dopamine High:** Solving a tough clue triggers a rush of dopamine. It's a "flow state" that lowers stress and makes your brain feel like it just won a marathon.
- **Building "Back Alleys":** Puzzles build **Cognitive Reserve**. Think of it as creating secret side streets in your brain; if the main road gets "blocked" by aging, your thoughts can still zip around through the shortcuts you built.

### The "Cheat Sheet" for Brain Gains

**Do This ✅**
- **Embrace the Struggle.** If it's too easy, your brain is on cruise control.
- **Make it a Ritual.** Consistency is where the 10-year benefit lives.
- **Try a Pen.** Tactile writing engages more brain regions than tapping.

**Not That ❌**
- **Stay in your comfort zone.** No "struggle" means no "growth."
- **Binge-puzzling.** Doing 50 in one day then stopping for a month doesn't count.
- **Relying solely on "Hint" buttons.** (We see you!)

### The Bottom Line

In a world that's constantly trying to distract us, sitting down with a puzzle is a rare win-win: it's fun, it's relaxing, and it keeps your brain from realizing it's getting older.

So, the next time you're stumped on 14-Down, don't give up. You aren't just playing a game—you're time-traveling.

**Happy Puzzling!**

What's your go-to puzzle when you need a brain boost?`,
  },
  {
    slug: "origin-of-word-games",
    title: "A Brief History of Word Games: From Anagrams to Wordle",
    description:
      "Word games are older than you think. A short history from ancient anagrams and acrostics to crosswords, Scrabble, and the daily-puzzle era.",
    date: "2026-05-07",
    readingMinutes: 5,
    tags: ["word games", "history", "puzzles"],
    category: "History",
    coverImage: coverHistoryOfWordGames,
    coverAlt: "Vintage flat-lay of scattered Scrabble letter tiles, a hand-drawn crossword grid with a single red square, and a quill pen on cream paper.",
    body: `## From Typos to Triumphs: A Quick History of Your Favorite Word Puzzles

Have you ever wondered if your favorite Sunday morning ritual was a stroke of genius or a total fluke? As it turns out, the history of **word puzzles** is just as twisty and surprising as the clues themselves.

If you're a fan of **brain games**, grab a coffee and settle in—here is how the world of wordplay actually began.

### The Billion-Dollar Typo

Believe it or not, the modern crossword was born from a printing error. In 1913, journalist **Arthur Wynne** created a diamond-shaped game for the *New York World* called "Word-Cross."

When the paper went to press, the typesetter accidentally flipped the name to **"Cross-Word."** Wynne loved the rhythm of the mistake, the public loved the game, and the rest is history. Next time you make a typo, just remember: you might be inventing the next global sensation!

### From "Sinful Waste" to Gold Standard

It's hard to imagine now, but the **New York Times**—now the undisputed home of the elite crossword—originally hated them. In the 1920s, they called the puzzles a "primitive form of mental exercise" and a "sinful waste of time."

It took the stress of World War II in 1942 for the editors to change their minds, realizing that readers needed a productive distraction. Today, finishing a Friday *Times* puzzle is basically a badge of honor for puzzle enthusiasts everywhere.

### 3 Quick Facts for Your Next Trivia Night

 * **A "Clue" was a Ball of String:** The word "clue" comes from the Middle English word *clew*, meaning a ball of yarn. It refers to the myth of Theseus using a string to find his way out of a labyrinth.

 * **The Ancient Roots:** Long before the newspaper, the Romans were playing with **Sator Squares**—5x5 grids of letters found in the ruins of Pompeii that read the same in every direction.

 * **Secret Agent Training:** During WWII, British intelligence used a difficult crossword in the *Daily Telegraph* to recruit codebreakers. If you could solve it in under 12 minutes, you were hired to help crack the Enigma code!

### Why We're Still Hooked

Whether you're a **Wordle** addict, a **Scrabble** pro, or a **crossword** traditionalist, word puzzles tap into a deep human desire to find order in chaos. They aren't just "mental exercise"—they're a connection to a history of linguistic rebels and accidental geniuses.

**What's your favorite word game to kick off the day? !**`,
  },
];

export const getPostBySlug = (slug: string) =>
  POSTS.find((p) => p.slug === slug);

export const getSortedPosts = () =>
  [...POSTS].sort((a, b) => (a.date < b.date ? 1 : -1));
